import React, {useEffect, useState} from 'react'
import moment from 'moment'
import {resolveSignatureDisplayUrl} from './digital-vos-form/resolveVosSignatureUrls'
import {formatVosTravelTime} from './vosFormatters'
import {
  computeVosDurations,
  formatVosDurationMinutes,
  readOsiBreakTimeMinutes,
} from './vosDurationUtils'
import './manualVosMeetingSummary.css'

const CLIENT_APPROVAL_LABELS: Record<number, string> = {
  0: 'NA',
  1: 'Pending',
  2: 'Accepted',
  3: 'Disputed',
  4: 'Approved',
}

type Props = {
  meetingSummary: Record<string, unknown>
}

function fmtClock(v: unknown): string {
  if (!v) return '—'
  const m = moment(String(v))
  return m.isValid() ? m.format('hh:mm A') : '—'
}

function fmtApprovedDate(v: unknown): string {
  if (!v) return '—'
  const m = moment(String(v))
  return m.isValid() ? m.format('MM/DD/YYYY, dddd h:mm A') : '—'
}

function durationForRange(
  start: unknown,
  end: unknown,
  summary: Record<string, unknown>,
  fallbackDisplay?: unknown
): string {
  if (fallbackDisplay && String(fallbackDisplay).trim()) return String(fallbackDisplay)
  if (!start || !end) return '—'
  const breakMinutes = readOsiBreakTimeMinutes(summary)
  const deductionEnabled = Boolean(
    summary.isBreakTimeDeductionEnabled ?? summary.IsBreakTimeDeductionEnabled
  )
  const {netMinutes} = computeVosDurations(
    String(start),
    String(end),
    breakMinutes,
    deductionEnabled
  )
  return formatVosDurationMinutes(netMinutes)
}

function DetailRow({label, value}: {label: string; value: React.ReactNode}) {
  return (
    <div className='mvos-detail-row'>
      <i className='fa-solid fa-clock me-2 text-muted mt-1' style={{fontSize: '12px', width: 14}} />
      <span>
        <span className='fw-bold me-1'>{label}</span>
        {value}
      </span>
    </div>
  )
}

function MileageRow({value}: {value: React.ReactNode}) {
  return (
    <div className='mvos-detail-row'>
      <i className='fa-solid fa-car me-2 text-muted mt-1' style={{fontSize: '12px', width: 14}} />
      <span>
        <span className='fw-bold me-1'>Mileage Of Drive (miles):</span>
        {value}
      </span>
    </div>
  )
}

function TravelRow({hours, minutes}: {hours?: unknown; minutes?: unknown}) {
  const h = hours != null ? Number(hours) : null
  const m = minutes != null ? Number(minutes) : null
  return (
    <div className='mvos-detail-row'>
      <i
        className='fa-solid fa-briefcase me-2 text-muted mt-1'
        style={{fontSize: '12px', width: 14}}
      />
      <span>
        <span className='fw-bold me-1'>Travel Time:</span>
        {formatVosTravelTime(h, m)}
      </span>
    </div>
  )
}

/** Invoice / Voucher sit under the Pending badge (wireframe), outside the inner summary card. */
export function ManualVosInvoiceVoucherLines({meetingSummary}: Props) {
  const invoiceNo = meetingSummary.invoiceNo as string | undefined
  const invoiceHeaderCode = meetingSummary.invoiceHeaderCode as number | undefined
  const voucherNo = meetingSummary.voucherNo as string | undefined
  const voucherHeaderCode = meetingSummary.voucherHeaderCode as number | undefined

  return (
    <div className='mvos-invoice-voucher mt-2 mb-2'>
      <span className='d-flex mb-1 align-items-center'>
        <i className='fa-solid fa-file me-2 text-muted' style={{fontSize: '12px'}} />
        <span className='fw-bold me-2'>Invoice ID :</span>
        {invoiceNo && invoiceHeaderCode ? (
          <a
            href={`/invoice/${invoiceHeaderCode}`}
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary text-decoration-underline'
          >
            {invoiceNo}
          </a>
        ) : (
          <span className='text-muted'>—</span>
        )}
      </span>
      <span className='d-flex align-items-center'>
        <i className='fa-solid fa-tag me-2 text-muted' style={{fontSize: '12px'}} />
        <span className='fw-bold me-2'>Voucher ID :</span>
        {voucherNo && voucherHeaderCode ? (
          <a
            href={`/voucher/${voucherHeaderCode}`}
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary text-decoration-underline'
          >
            {voucherNo}
          </a>
        ) : (
          <span className='text-muted'>—</span>
        )}
      </span>
    </div>
  )
}

function VosColumn({
  title,
  start,
  end,
  mileage,
  travelH,
  travelM,
  summary,
  durationDisplay,
}: {
  title: string
  start: unknown
  end: unknown
  mileage: unknown
  travelH: unknown
  travelM: unknown
  summary: Record<string, unknown>
  durationDisplay?: unknown
}) {
  return (
    <div className='col-md-6'>
      <h6 className='fw-bold mb-3'>{title}</h6>
      <DetailRow label='Start Time:' value={fmtClock(start)} />
      <DetailRow label='End Time:' value={fmtClock(end)} />
      <DetailRow
        label='Duration:'
        value={durationForRange(start, end, summary, durationDisplay)}
      />
      <MileageRow value={mileage != null && mileage !== '' ? String(mileage) : '—'} />
      <TravelRow hours={travelH} minutes={travelM} />
    </div>
  )
}

export function ManualVosMeetingSummaryCard({meetingSummary}: Props) {
  const bulkStatus = Number(meetingSummary.bulkVosClientApprovalStatus ?? 0)
  const clientApprovedLabel = CLIENT_APPROVAL_LABELS[bulkStatus] ?? 'NA'
  const bulkSignRaw =
    (meetingSummary.bulkApproverSignUrl as string | undefined) ??
    (meetingSummary.BulkApproverSignUrl as string | undefined)
  const [bulkSignDisplayUrl, setBulkSignDisplayUrl] = useState<string | null>(null)

  const bulkClientApproverName =
    (
      (meetingSummary.bulkApproverName as string | undefined) ??
      (meetingSummary.BulkApproverName as string | undefined)
    )?.trim() || '—'

  const vosStatus = Number(meetingSummary.status)
  const isDecided = vosStatus === 2 || vosStatus === 3 || vosStatus === 4
  const decisionLabel = vosStatus === 3 ? 'Rejected' : vosStatus === 4 ? 'Disputed' : 'Approved'

  const financeApprovedBy = (
    (meetingSummary.financeApprovedByName as string | undefined) ??
    (meetingSummary.FinanceApprovedByName as string | undefined)
  )?.trim()

  const lastModifiedBy = (
    (meetingSummary.lastModifiedBy as string | undefined) ??
    (meetingSummary.LastModifiedBy as string | undefined)
  )?.trim()

  const financeApprovedDate =
    meetingSummary.financeApprovedAt ??
    meetingSummary.FinanceApprovedAt ??
    meetingSummary.bulkApprovedAt ??
    meetingSummary.BulkApprovedAt

  const lastModifiedDate =
    meetingSummary.lastModifiedDateTime ?? meetingSummary.LastModifiedDateTime

  const decidedBy = (vosStatus === 2 && financeApprovedBy) || (isDecided && lastModifiedBy) || '—'

  const decidedDate =
    (vosStatus === 2 && financeApprovedDate) || (isDecided ? lastModifiedDate : null)

  useEffect(() => {
    if (!bulkSignRaw?.trim()) {
      setBulkSignDisplayUrl(null)
      return
    }
    let cancelled = false
    resolveSignatureDisplayUrl(bulkSignRaw).then((url) => {
      if (!cancelled) setBulkSignDisplayUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [bulkSignRaw])

  useEffect(() => {
    return () => {
      if (bulkSignDisplayUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(bulkSignDisplayUrl)
      }
    }
  }, [bulkSignDisplayUrl])

  return (
    <div className='mvos-summary-card border rounded p-4 mb-0'>
      <div className='mvos-submission-meta fw-bold'>
        <div className='mb-1'>Submitted via: Manual</div>
        <div>Client Approved: {clientApprovedLabel}</div>
      </div>

      <div className='row g-4 mb-3 mvos-comparison-columns'>
        <VosColumn
          title='Client Submitted Details'
          start={meetingSummary.clientSubmittedStartDate}
          end={meetingSummary.clientSubmittedEndDate}
          mileage={meetingSummary.clientSubmittedMilage}
          travelH={meetingSummary.clientSubmittedTravelTimeH}
          travelM={meetingSummary.clientSubmittedTravelTimeM}
          summary={meetingSummary}
        />
        <VosColumn
          title='Interpreter Submitted Details'
          start={
            meetingSummary.interpreterStartDate ??
            meetingSummary.InterpreterStartDate ??
            (bulkStatus === 0 ? meetingSummary.startDate : undefined)
          }
          end={
            meetingSummary.interpreterEndDate ??
            meetingSummary.InterpreterEndDate ??
            (bulkStatus === 0 ? meetingSummary.endDate : undefined)
          }
          mileage={
            meetingSummary.interpreterMilage ??
            meetingSummary.InterpreterMilage ??
            (bulkStatus === 0 ? meetingSummary.milage : undefined)
          }
          travelH={
            meetingSummary.interpreterTravelTimeH ??
            meetingSummary.InterpreterTravelTimeH ??
            (bulkStatus === 0 ? meetingSummary.travalTimeH : undefined)
          }
          travelM={
            meetingSummary.interpreterTravelTimeM ??
            meetingSummary.InterpreterTravelTimeM ??
            (bulkStatus === 0 ? meetingSummary.travalTimeM : undefined)
          }
          summary={meetingSummary}
        />
      </div>

      {meetingSummary.bulkClientRemarks ? (
        <div className='mvos-remarks p-3 mb-4'>
          <div className='fw-bold small text-uppercase mb-2'>Client Remarks</div>
          <div className='text-gray-800'>{String(meetingSummary.bulkClientRemarks)}</div>
        </div>
      ) : null}

      <div className='border-top pt-4'>
        <h6 className='fw-bold mb-3'>Approved Details</h6>
        <div className='row g-4'>
          <div className='col-md-7'>
            <DetailRow label='Start Time:' value={fmtClock(meetingSummary.startDate)} />
            <DetailRow label='End Time:' value={fmtClock(meetingSummary.endDate)} />
            <DetailRow
              label='Duration:'
              value={durationForRange(
                meetingSummary.startDate,
                meetingSummary.endDate,
                meetingSummary,
                meetingSummary.durationDisplay
              )}
            />
            <MileageRow
              value={
                meetingSummary.milage != null && meetingSummary.milage !== ''
                  ? String(meetingSummary.milage)
                  : '—'
              }
            />
            <TravelRow
              hours={meetingSummary.travalTimeH}
              minutes={meetingSummary.travalTimeM}
            />
            <div className='mvos-approved-meta'>
              <div className='mvos-detail-row'>
                <span className='fw-bold me-1'>{decisionLabel} by:</span>
                {decidedBy}
              </div>
              <div className='mvos-detail-row'>
                <span className='fw-bold me-1'>{decisionLabel} Date:</span>
                {fmtApprovedDate(decidedDate)}
              </div>
            </div>
          </div>
          <div className='col-md-5'>
            <div className='mb-3'>
              <span className='fw-bold small text-uppercase'>Approver Name: </span>
              <span>{bulkClientApproverName}</span>
            </div>
            <div className='fw-bold small text-uppercase mb-2'>Approver Signature</div>
            <div className='mvos-signature-box'>
              {bulkSignRaw ? (
                bulkSignDisplayUrl ? (
                  <img
                    src={bulkSignDisplayUrl}
                    alt='Approver signature'
                    style={{maxHeight: 64, maxWidth: '100%'}}
                  />
                ) : (
                  <span className='text-muted small'>Loading signature…</span>
                )
              ) : (
                <span className='text-muted small'>—</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
